import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { commonComponentImages } from '@images/common-components-images';
import { commonImages } from '@images/common-images';
import { DashService } from '@modules/dash-board/dash.service';
import { ActingAsService } from '@services/acting-as.service';
import { ApiService } from '@services/api/api.service';
import { RemmiReleasesService } from '@services/api/remmi-releases.service';
import { SettingService } from '@services/api/setting.service';
import { AuthService } from '@services/auth.service';
import { AlertService } from '@services/helpers/alert.service';
import { Crypto } from '@services/helpers/encryption-utils';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { LeadManagementService } from '@services/lm-service/lead-management.service';
import { SharedDataService } from '@services/shared-data.service';
import { ActivityTrackerService } from '@services/user-activity/activity-tracker.service';
import { MessageService } from '@services/util/message.service';
import { WS } from '@services/util/WebSocket.service';
import { ReduxService } from '@state-management/redux.service';
import { ToastrService } from 'ngx-toastr';
import { MenuItem } from 'primeng/api';
import { Subject, Subscription, debounceTime } from 'rxjs';
import { PreviewListingComponent } from '../listing-preview/preview-listing/preview-listing.component';
import { NgSelectComponent } from '@ng-select/ng-select';
import { HeaderNotesComponent } from './header-notes/header-notes.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('previewModel') previewModel: PreviewListingComponent;

  images = commonComponentImages;
  common_images = commonImages;
  public AuthUser: any;
  public notifications: any[] = [];
  public env = environment;
  public offices: any;
  public copyOfficesList: any;
  activeOffice: any;
  isNotificationUpdating = false;
  addAllModule: boolean = false;
  ProfileLinks: MenuItem[];
  module_value: any;
  globalFilterModule: any;

  module: any = [{ module_name: 'Contact' }, { module_name: 'Property' }, { module_name: 'Lead' }, { module_name: 'Whiteboard' }];

  global_module: any = [
    { module_name: 'Contact', module_value: 'Contact', flag: 'contacts' },
    { module_name: 'Contract', module_value: 'contracts', flag: 'contracts' },
    { module_name: 'EOI', module_value: 'eoi', flag: 'eoi' },
    { module_name: 'Lead', module_value: 'leads', flag: 'leads' },
    // { module_name: 'Market Place', module_value: 'market_place', flag: 'market_place' },
    { module_name: 'Property', module_value: 'Property', flag: 'properties' },
    { module_name: 'Project', module_value: 'Project', flag: 'projects' },
    { module_name: 'Staff', module_value: 'staff', flag: 'staff' },
    // { module_name: 'Task', module_value: 'tasks', flag: 'my_tasks' },
    { module_name: 'Lot', module_value: 'units', flag: 'lot' },
  ];

  subscription = new Subscription();
  private resizeSubscription: Subscription;
  /** Router targets and thumbnail per notification, built once per socket push so the template binds stable references. */
  notificationTargets = new Map<any, { link: string[]; queryParams: Record<string, string> | null; thumb: string }>();
  public userRole;
  temp_listing_preview: any[] = [];
  screenWidth: any;
  mobToggle: any = false;
  constructor(
    public alertService: AlertService,
    public router: Router,
    public Auth: AuthService,
    public Api: ApiService,
    public redux: ReduxService,
    private messageService: MessageService,
    public toastr: ToastrService,
    public ws: WS,
    public lmService: LeadManagementService,
    public localStorageService: LocalStorageService,
    public sharedDataService: SharedDataService,
    private dash: DashService,
    private _settingService: SettingService,
    private activityTracker: ActivityTrackerService,
    private releasesService: RemmiReleasesService,
    private actingAs: ActingAsService
  ) {}

  ngOnInit() {
    if (!this.router.url.includes('/report/edit-report')) {
      localStorage.removeItem('eye');
    }

    this.getUser();
    // Pending feature banners load once per session here, so they are ready before any module page asks.
    this.releasesService.ensureLoaded();
    this.subscription.add(
      this.messageService.getMessage().subscribe({
        next: value => {
          if (value?.userUpdated) {
            this.getUser();
          }
        },
      })
    );
    // Initialize user-related data and services
    this.getOffices();
    this.getMySubOffices();
    // Delayed initialization for role and MFA settings
    setTimeout(() => {
      this.userRole = Crypto.decrypt(this.localStorageService.GetItem('role'));
      this.getMFAEnabled();
    }, 1000);

    // Define profile links with associated actions
    this.ProfileLinks = [
      {
        items: [
          {
            label: 'My Profile',
            icon: 'pi pi-user',
            routerLink: ['/setting/profile/info'],
          },
          {
            // Release history: every published release the user can see, even ones already dismissed.
            label: "What's New",
            icon: 'pi pi-megaphone',
            command: () => this.releasesService.openHistory(),
          },
          {
            label: 'Logout',
            icon: 'pi pi-sign-out',
            command: () => this.logout(),
          },
        ],
      },
    ];

    // Set up search subject with debounce
    this.subscription.add(
      this.searchSubject.pipe(debounceTime(500)).subscribe(rex => {
        this.globalSearch(rex?.value?.trim());
      })
    );

    this.subscription.add(
      this.alertService.image_update.subscribe({
        next: (response: any) => {
          if (response) {
            this.getUser();
          }
        },
      })
    );

    // Handle screen resizing for mobile toggle
    this.resizeSubscription = this.dash.screenSize$.subscribe(size => {
      this.screenWidth = size.width;
      this.mobToggle = this.screenWidth < 830;
    });

    // Subscribe to web socket notifications
    this.subscription.add(
      this.ws.getNoti().subscribe((rs: any) => {
        if (!this.isNotificationUpdating) {
          this.notifications = rs;
          this.buildNotificationTargets();
          // Filter notifications for listing approval
          let tempNotificationListingPreview: any[] = this.notifications?.filter(
            notify =>
              notify?.data?.link?.includes('listing-approval-popup') ||
              notify?.data?.title === 'Listing Approval Comment' ||
              notify?.data?.title === 'Listing Approved'
          );
          // Update shared data service if notification list changes
          if (this.temp_listing_preview?.length !== tempNotificationListingPreview?.length) {
            this.temp_listing_preview = tempNotificationListingPreview;
            this.sharedDataService.approvalNotificationUpdateEventHandler(this.temp_listing_preview);
          }
        }
      })
    );
  }

  ngOnDestroy() {
    // Unsubscribe from the main subscription if it exists
    this.subscription?.unsubscribe();

    // Check if the resize subscription exists and unsubscribe from it
    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe();
    }
    clearTimeout(this.searchTrackTimer);
  }

  getUser() {
    this.redux.getUser().subscribe({
      next: response => {
        console.log('response :>> ', response);
        this.Auth.setAuthUser(response?.data);
        /**
         * Store the user data in the component
         */
        this.AuthUser = JSON.parse(JSON.stringify(response?.data ?? response));
        /**
         * Store the active office in the component
         */
        this.activeOffice = this.AuthUser.active_office;

        /**
         * If the user is deactivated, log them out
         */
        if (this.AuthUser?.deactive === true) {
          this.logout();
        } else {
          // Safety net: `uid` is stored just above, so a socket the layout could not open earlier
          // still gets opened. No-op when one is already open for this user. Services are omitted
          // on purpose — the layout's PrimeNG ones are already registered, and this component's
          // MessageService is a different class.
          this.ws.connect(response?.data?._id ?? response?.data?.id);
        }
      },
      error: () => {
        /* 401 etc. handled by AuthTokenInterceptor (logout / redirect) */
      },
    });
  }

  getOffices() {
    /**
     * Get the offices of the current user
     * @param is_short {boolean} Whether to return short or full office data
     * @returns Promise with an array of offices
     */
    this.redux.getMyOffice().subscribe(data => {
      /**
       * Store the offices in the component
       */
      let officeList = JSON.parse(JSON.stringify(data.data));
      this.copyOfficesList = this.offices = officeList.sort((a, b) => a?.name.localeCompare(b?.name));
      /**
       * Iterate over the offices and add an 'id' property to each office
       *   This is to make the office compatible with the select component
       */
      this.offices.forEach(itm => {
        if (itm._id) {
          itm.id = itm._id;
        }
      });
    });
  }

  subOffices: any[] = [];
  activeSubOffices: string[] = [];

  /** Master admins always switch sub offices; anyone else needs access to the QA Tester office. */
  // get canSwitchSubOffices(): boolean {
  //   if (this.userRole?.toLowerCase() === 'master admin' || this.userRole?.toLowerCase() === 'operation manager') {
  //     return true;
  //   }
  //   return (this.copyOfficesList || []).some(itm => itm?.name?.toLowerCase() === 'qa tester');
  // }

  /**
   * Options for the sub office dropdown: the sub offices assigned to the logged-in user,
   * each already flagged with whether the user has it selected.
   */
  getMySubOffices() {
    this._settingService.getMySubOffices().subscribe({
      next: response => {
        this.subOffices = response?.data || [];
        this.activeSubOffices = this.subOffices.filter(itm => itm?.is_selected).map(itm => itm?._id);
        // Publish so open lists/grids filter by it (no-op if it matches what they already used).
        this._settingService.setActiveSubOffices(this.activeSubOffices);
      },
    });
  }

  /** Tooltip for the "+N" badge — the selected sub offices the collapsed chip doesn't show. */
  subOfficeNames(items: any[]) {
    return (items || [])
      .slice(1)
      .map(itm => itm?.office_name)
      .join(', ');
  }

  updateActiveSubOffices() {
    // Publish first so the open list/grid re-filters immediately rather than waiting on the save.
    this._settingService.setActiveSubOffices(this.activeSubOffices || []);

    this._settingService
      .saveActiveSubOffices({
        user_id: this.Api.getUserId(),
        active_sub_offices: this.activeSubOffices || [],
      })
      .subscribe({
        error: () => {
          this.toastr.error('Something wrong try again.');
          // Save failed — pull the stored selection back so the UI stops showing an applied
          // filter the server never accepted.
          this.getMySubOffices();
        },
      });
  }

  @ViewChild('officeSelect') officeSelect!: NgSelectComponent;
  openOfficeDropdown() {
    this.officeSelect.open();
    setTimeout(() => this.officeSelect?.focus(), 0);
  }

  isMobileMenuOpen = false;
  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  Enabled_MFA: any;
  getMFAEnabled() {
    /**
     * MFA is a property of the human who authenticated, not of an impersonated agent.
     * While a Sales Assistant is acting-as an agent, `getUserId()` is the agent's id, so this
     * would enforce MFA against the agent — who may have none — and log the assistant out.
     * The assistant already cleared their own MFA at login, so skip the check entirely.
     */
    if (this.actingAs.isActingAs()) {
      this.Enabled_MFA = null;
      this.alertService.mfaError(null);
      return;
    }

    /**
     * Fetch the user's MFA type and handle the response.
     */
    this.Api.get('/user-mfa-type/' + this.Api.getUserId()).subscribe(res => {
      // Store the MFA type in the component
      this.Enabled_MFA = res?.data;

      // Report the MFA type or lack thereof
      this.alertService.mfaError(this.Enabled_MFA);

      // If no MFA is enabled and there's no grace period, log out the user
      if (this.Enabled_MFA && this.Enabled_MFA.authentication_type == null && !this.Enabled_MFA.hasOwnProperty('grace_period_end')) {
        this.logout();
      }
    });
  }

  async reloadApp() {
    const data = {
      user_id: this.Api.getUserId(),
      reloaded: true,
    };

    this.Api.post('/force-to-reload', data).subscribe(async () => {
      try {
        // 1. Clear Cache Storage (Cache API — used by service workers / PWAs)
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
        }

        // 2. Unregister service workers
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map(reg => reg.unregister()));
        }

        // 3. (Optional) clear app storage if you want a truly fresh state
        // localStorage.clear();
        // sessionStorage.clear();
      } catch (e) {
        console.warn('Cache clearing failed:', e);
      }

      // 4. Hard reload with cache-busting query param
      window.location.replace(window.location.pathname + '?nocache=' + Date.now());
    });
  }

  // reloadApp() {
  //   /**
  //    * Post a request to mark the user as having reloaded the app
  //    * @param data {object} The data to post to the server
  //    */
  //   let data = {
  //     user_id: this.Api.getUserId(),
  //     reloaded: true,
  //   };
  //   this.Api.post('/force-to-reload', data).subscribe(() => {
  //     /**
  //      * Reload the page once the request is successful
  //      */
  //     window.location.href = window.location.href + '?nocache=' + new Date().getTime();
  //   });
  // }

  emptySearch() {
    this.prop_address = null;
    this.isSearchTrue = false;
  }

  selectItem() {
    this.isSearchTrue = false;
    this.prop_address = null;
  }

  // openRightBarWhenClickPlusButton: boolean = false;
  globalSearchValue: any;
  onClickedOutside(e: Event) {
    if (e['bol'] === false && !this.globalSearchValue) {
      this.isSearchTrue = false;
    }
  }

  selectModule(e) {
    this.globalSearchValue = this.globalFilterModule ? this.globalFilterModule : e?.module_value;
    this.globalSearch();
    setTimeout(() => {
      this.globalSearchValue = null;
    }, 2000);
  }

  itemsToShow = 5;
  S3Path = environment.S3 + '/';
  filteredRecord: { [key: string]: any[] } = {
    propertyData: [],
    listingData: [],
    archiveListingData: [],
    contacts: [],
    projects: [],
    units: [],
    tasks: [],
    leads: [],
    market_place: [],
    contracts: [],
    eoi: [],
    staff: [],
    deactiveStaff: [],
    exProject: [],
  };
  prop_address: any;
  isSearchTrue: boolean = false;
  searchSubject = new Subject<any>();
  onSearch(method) {
    const searchValue = this.prop_address?.trim();
    this.searchSubject.next({ id: method, value: searchValue });
  }

  toggleSearch() {
    this.globalSearch(this.prop_address?.trim());
  }

  globalSearch(val = null) {
    /**
     * If the globalFilterModule is set, use it as the query param. Otherwise, use an empty array.
     * @param query The query param to pass to the endpoint.
     */
    let query = this.globalFilterModule ? [this.globalFilterModule] : [];
    let value = val === null ? this.prop_address : val;
    this.userRole = Crypto.decrypt(this.localStorageService.GetItem('role'));

    if (!this.prop_address) {
      this.isSearchTrue = false;
      return;
    }
    this.trackSearch(`${value}|${this.globalFilterModule || ''}`);
    /**
     * Construct the endpoint URL based on the user role.
     * If the user role is 'contacts', use the external-user-global-search endpoint.
     * Otherwise, use the global-search endpoint.
     * @param endpoint The constructed endpoint URL.
     */
    const endpoint =
      this.userRole?.toLowerCase() === 'contacts'
        ? `/global-search/external/?office_id=${this.Api.getOfficeId()}&project_name=${value}`
        : `/global-search/?office_id=${this.Api.getOfficeId()}&property_address=${value}&filter=${JSON.stringify(query)}`;

    /**
     * Make the HTTP request to the endpoint and handle the response.
     * @param res The response from the endpoint.
     */
    this.Api.get(endpoint).subscribe({
      next: res => {
        this.isSearchTrue = true;
        // Reset all filtered data
        Object.keys(this.filteredRecord).forEach(key => (this.filteredRecord[key] = []));
        // Populate filteredData dynamically
        res?.data?.forEach(x => {
          Object.keys(this.filteredRecord).forEach(key => {
            if (x?.[`${key}`] && x[`${key}`].length > 0) {
              this.filteredRecord[key] = x[`${key}`];
            }
          });
        });
      },
    });
  }

  /**
   * Search runs as you type, so a search counts once the term has settled for 1s and differs from
   * the last one counted. The term is kept here for that comparison only — it is never sent.
   */
  private lastTrackedSearch = '';
  private searchTrackTimer: ReturnType<typeof setTimeout> = null;
  private trackSearch(searchKey: string): void {
    clearTimeout(this.searchTrackTimer);
    this.searchTrackTimer = setTimeout(() => {
      if (searchKey !== this.lastTrackedSearch) {
        this.lastTrackedSearch = searchKey;
        this.activityTracker.track('search', { meta: { module: 'global' } });
      }
    }, 1000);
  }

  showMore(val) {
    this.isSearchTrue = false;
    this.router.navigateByUrl('/global/advance-search/' + val);
  }
  logout() {
    // Log the user out
    this.Auth.logOut();
    this.alertService.mfaError(null);
  }

  updateUserOffice() {
    let office = {
      active_office: this.activeOffice,
      first_name: this.AuthUser.first_name,
      last_name: this.AuthUser.last_name,
      email: this.AuthUser.email,
      mobile: this.AuthUser.mobile,
      color: this.AuthUser.color,
      listing_approval_email_notification: this.AuthUser.listing_approval_email_notification,
      listing_approval_push_notification: this.AuthUser.listing_approval_push_notification,
      listing_approval_web_notification: this.AuthUser.listing_approval_web_notification,
    };
    this.Api.post('/me', office).subscribe({
      next: data => {
        this.Auth?.updateOfficeId(data?.data?.active_office);
        this.Auth.changeUserOffice('change default office');
        this.localStorageService.SetItem('officeid', Crypto.encrypt(data.data.active_office));
        this.toastr.success('User office has been updated');
        this.redux.setUser(null);
        this.redux.getUser(true).subscribe({
          next: response => {
            let user_value = data?.data?.active_office?.length > 0 ? { ...response?.data, active_office: data?.data?.active_office } : response?.data;
            this.Auth.setAuthUser(user_value);
            console.log('user_value :>> ', user_value);
            // this.store.dispatch(new SetLoggedinUserStatusAction({ data: user_value }));

            // this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            //   this.router.navigate([this.router.url]);
            // });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          },
          error: _ => {
            this.toastr.error('Something wrong try again.');
          },
        });
        this.lmService.contactNavUpdated.next(true);
        this.Api.get('/offices/byid/' + data.data.active_office).subscribe({
          next: response => {
            let off = response.data;
            if (off && off.global) {
              this.localStorageService.SetItem('isGlobalOffice', Crypto.encrypt('true'));
            } else {
              this.localStorageService.SetItem('isGlobalOffice', Crypto.encrypt('false'));
            }
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          },
          error: () => {
            this.toastr.error('Something wrong try again.');
          },
        });
      },
      error: error => {
        if (error.message && error.status_code !== 500) {
          this.toastr.error(error.error.message);
        } else {
          this.toastr.error('Something wrong with the form. Please check and try again.');
        }
      },
    });
  }

  listing_preview_id: Subject<any> = new Subject<any>();
  notificationRead(notification) {
    /**
     * Remove the notification from the list and mark it as read.
     * If the notification is for a listing approval, open the listing preview popup.
     * @param notification The notification to be processed.
     */
    this.Api.post('/notifications/read', { read: notification?._id ? notification._id : notification?.data?.id }).subscribe(() => {
      // Handle the response if necessary
    });
    const id = notification._id ? notification._id : notification.data.id;
    // Remove the notification from the list
    this.notifications.splice(this.notifications.indexOf(notification), 1);
    // A Horizon notification opens the lead on its Horizon tab; the lead detail consumes `aiLeadTab` once on load.
    if (this.isHorizonLeadNotification(notification)) {
      this.lmService.aiLeadTab = 'horizon';
    }
    if (notification?.data?.link?.includes('listing-approval-popup')) {
      // Open the listing preview popup
      let preview_lisitng_id: string[] = notification?.data?.link?.split('/');
      this.listing_preview_id.next(preview_lisitng_id[preview_lisitng_id?.length - 1]);
      this.previewModel.show();
    } else if (id) {
      // Mark the notification as read
      this.isNotificationUpdating = true;
      // this.Api.post('/notifications/read', { read: id }).subscribe(res => {
      //   this.isNotificationUpdating = false;
      // });
      setTimeout(() => {
        this.isNotificationUpdating = false;
      }, 2000);
    }
  }
  readAllNotifications() {
    /**
     * Mark all notifications as read and clear the notification list.
     */
    this.notifications = []; // Clear all notifications from the list

    // Make an API call to mark all notifications as read
    this.Api.post('/notifications/read', { read: 'all' }).subscribe(() => {
      // Handle the response if necessary
    });
  }
  thumb(thumb: string): string {
    // Already a usable URL (base64 data URI, or an absolute http(s) URL from elsewhere) —
    // prepending the S3 base to either would produce an unresolvable, mangled URL.
    if (thumb?.startsWith('data:image/') || thumb?.startsWith('http://') || thumb?.startsWith('https://')) {
      return thumb;
    }

    // Otherwise, it's an S3 key: return the full S3 URL.
    return `${this.env.S3}/${thumb}`;
  }

  /** CSS class for global-search result title width (desktop vs mobile header). */
  get addressLineClass(): string {
    return this.mobToggle ? 'mob-w-380' : 'w-380';
  }

  /** True when any global search bucket has results (matches template filter keys). */
  get hasGlobalSearchResults(): boolean {
    const f = this.filteredRecord;
    if (!f) {
      return false;
    }
    return (
      (f.propertyData?.length ?? 0) > 0 ||
      (f.listingData?.length ?? 0) > 0 ||
      (f.archiveListingData?.length ?? 0) > 0 ||
      (f.contacts?.length ?? 0) > 0 ||
      (f.projects?.length ?? 0) > 0 ||
      (f.units?.length ?? 0) > 0 ||
      (f.tasks?.length ?? 0) > 0 ||
      (f.leads?.length ?? 0) > 0 ||
      (f.market_place?.length ?? 0) > 0 ||
      (f.contracts?.length ?? 0) > 0 ||
      (f.eoi?.length ?? 0) > 0 ||
      (f.staff?.length ?? 0) > 0 ||
      (f.deactiveStaff?.length ?? 0) > 0
    );
  }

  stripAustralia(address: string | undefined | null): string {
    if (address == null || address === '') {
      return '';
    }
    return address.replace('Australia', '');
  }

  /**
   * A record id sitting on the path rather than the query string, e.g.
   * `/projects/edit_/<projectId>/eoi/<eoiId>`. The EOI screen only deep-links
   * from `?id=`, so such a link is rewritten into path + query below.
   */
  private trailingIdSegment(path: string): { base: string; id: string } | null {
    const match = path.match(/^(.*\/eoi)\/([A-Za-z0-9_-]{6,})\/?$/);
    return match ? { base: match[1], id: match[2] } : null;
  }

  /**
   * A Horizon notification for a lead: its link asks for `tab=horizon`
   * (`/projects/edit_/<projectId>/lead?id=<leadId>&tab=horizon`), or it has no
   * link but names the lead and its project.
   */
  private isHorizonLeadNotification(notification: any): boolean {
    const link = notification?.data?.link;
    if (link) {
      const query = String(link).split('?')[1];
      return !!query && new URLSearchParams(query).get('tab') === 'horizon';
    }
    return !!this.horizonLeadFallback(notification);
  }

  /** The lead to open for a notification without a link that carries `lead_id` and `project_id`. */
  private horizonLeadFallback(notification: any): { projectId: string; leadId: string } | null {
    const data = notification?.data;
    if (data?.link || !data?.lead_id || !data?.project_id) {
      return null;
    }
    return { projectId: String(data.project_id), leadId: String(data.lead_id) };
  }

  notificationRouterLink(notification: any): string[] {
    const link = notification?.data?.link;
    if (!link) {
      const lead = this.horizonLeadFallback(notification);
      return lead ? ['/projects', 'edit_', lead.projectId, 'lead'] : [''];
    }
    const s = String(link);
    const base = s.split('?')[0];
    // `/eoi/<id>` → `/eoi`; the id moves to queryParams, which is where the
    // EOI screen looks for it.
    const trailing = this.trailingIdSegment(base);
    if (trailing) {
      return [trailing.base];
    }
    return [base || s];
  }

  /**
   * Pull the record id out of the notification's link.
   *
   * Links are backend-authored, e.g. an External EOI submission sends
   * `/projects/edit_/<projectId>/eoi?id=<eoiId>`, which the EOI screen reads in
   * `checkIdInParams()` to open that EOI.
   *
   * **Every param on the link is forwarded**, so screens keyed on their own name
   * (Failed Dynamic Sync reads `lead_id`) still get it.
   *
   * On top of that, `id` is resolved for the screens that read it, because a
   * link carrying the right id in the wrong shape is the difference between the
   * EOI opening and the buyer's submission looking lost. In order:
   *  - `?id=<eoiId>` — the documented form
   *  - `?eoi_id=<eoiId>` — an alias
   *  - the first param on the link — the legacy "first value wins" rule
   *  - `/eoi/<eoiId>` — the id on the path
   *  - `data.eoi_id` on the notification payload itself
   */
  notificationQueryParams(notification: any): Record<string, string> | null {
    const link = notification?.data?.link;
    const payloadId = notification?.data?.eoi_id;
    if (!link) {
      const lead = this.horizonLeadFallback(notification);
      if (lead) {
        return { id: lead.leadId };
      }
      return payloadId ? { id: String(payloadId) } : null;
    }

    const s = String(link);
    const [path, query] = [s.split('?')[0], s.split('?')[1]];

    // Forward the link's own params verbatim.
    const queryParams: Record<string, string> = {};
    if (query) {
      new URLSearchParams(query).forEach((value, key) => (queryParams[key] = value));
    }
    // `tab=horizon` is handed to the lead detail through `aiLeadTab` (see notificationRead), not the URL.
    if (queryParams['tab'] === 'horizon') {
      delete queryParams['tab'];
    }

    // Then make sure `id` resolves, whichever shape carried it.
    if (!queryParams['id']) {
      const id =
        queryParams['eoi_id'] ?? Object.values(queryParams)[0] ?? this.trailingIdSegment(path)?.id ?? (payloadId ? String(payloadId) : undefined);
      if (id) {
        queryParams['id'] = id;
      }
    }

    return Object.keys(queryParams).length ? queryParams : null;
  }

  notificationThumbSrc(notification: any): string {
    const t = notification?.data?.thumb;
    if (t && t !== 'vendor') {
      return this.thumb(t);
    }
    return this.common_images?.face_image as string;
  }

  private buildNotificationTargets() {
    this.notificationTargets = new Map();
    (this.notifications || []).forEach(notification => {
      this.notificationTargets.set(notification, {
        link: this.notificationRouterLink(notification),
        queryParams: this.notificationQueryParams(notification),
        thumb: this.notificationThumbSrc(notification),
      });
    });
  }

  notificationTrackBy(_index: number, notification: any) {
    return notification?._id ?? notification?.data?.id ?? notification;
  }

  clear(item) {
    this.module_value = this.module_value.filter(contact => contact !== item);
  }

  openRightModal(item: any, addOrEdit: string): void {
    /**
     * Opens the right modal based on the module and `addOrEdit` type.
     * @param item The item to be edited or viewed.
     * @param addOrEdit The type of module to be edited or opened.
     */

    /**
     * Determines whether navigation should occur based on the new and current URLs.
     * @param url The target URL for navigation.
     * @param queryParams The query parameters for comparison.
     * @returns boolean Whether navigation should proceed.
     */
    const shouldNavigate = (url: string, queryParams: any = {}): boolean => {
      const currentUrl = this.router.url.split('?')[0]; // Get current route without query parameters
      const queryParamsString = new URLSearchParams(queryParams).toString();
      return currentUrl !== url || !this.router.url.includes(queryParamsString);
    };

    if (Array.isArray(item) && item.length > 0) {
      // If `item` is an array, iterate over it and open the respective modal for each module.
      item.forEach(x => {
        this.addAllModule = false;
        this.module_value = [];

        const moduleRoutes = {
          Contact: '/setting/office-contact',
          Property: '/property/properties',
          Lead: '/leads/lead-overview',
        };

        if (x?.module_name in moduleRoutes) {
          this.router.navigate([moduleRoutes[x?.module_name]], {
            queryParams: { id: 'createnew' },
          });
        } else if (x?.module_name === 'Whiteboard') {
          // Open the whiteboard
          this.router.navigateByUrl('/my-whiteboard');
        }
      });
    } else if (addOrEdit === 'edit_unit' && shouldNavigate('/listings/lot', { id: item?._id })) {
      // Open the unit edit modal
      this.router.navigate(['/listings/lot'], { queryParams: { id: item?._id } });
    } else if (addOrEdit === 'edit_EOI' && shouldNavigate('/listings/listing-eoi', { id: item?._id })) {
      // Open the EOI edit modal
      this.router.navigate(['/listings/listing-eoi'], { queryParams: { id: item?._id } });
    } else if (addOrEdit === 'edit_project') {
      // Open the project edit modal
      this.router.navigate(['/projects/edit_/' + item._id + '/price-list']);
    } else if (addOrEdit === 'edit_listing_property' && shouldNavigate('/listings/listing-properties', { id: item?._id })) {
      // Open the listing property edit modal
      this.router.navigate(['/listings/listing-properties'], { queryParams: { id: item?._id } });
    } else if (addOrEdit === 'edit_property' && shouldNavigate('/property/properties', { id: item?._id })) {
      // Open the property edit modal
      this.router.navigate(['/property/properties'], { queryParams: { id: item?._id } });
    }

    // Define route mappings for different `addOrEdit` cases
    const editRoutes: { [key: string]: { path: string; params?: any } } = {
      edit_unit: { path: '/listings/lot', params: { id: item?._id } },
      edit_EOI: { path: '/listings/listing-eoi', params: { id: item?._id } },
      edit_project: { path: `/projects/edit_/${item._id}/price-list` },
      edit_listing_property: { path: '/listings/listing-properties', params: { id: item?._id } },
      edit_property: { path: '/property/properties', params: { id: item?._id } },
      edit_lead: { path: '/leads', params: { id: item?._id } },
      edit_task: { path: '/tasks/all-tasks', params: { id: item?._id } },
      'market-place': { path: '/listings/market-place', params: { id: item?._id } },
    };

    // Handle specific cases requiring additional processing
    if (addOrEdit === 'edit_contact') {
      let name = item?.full_name?.trim() ? item?.full_name : item?.company_name;
      if (shouldNavigate('/setting/office-contact', { id: item?._id, full_name: name })) {
        this.router.navigate(['/setting/office-contact'], {
          queryParams: { id: item?._id, full_name: name },
        });
      }
      return;
    }

    if (addOrEdit === 'edit_contract') {
      let name = item?.buyer?.[0]?.full_name;
      if (shouldNavigate('/contracts', { id: item?._id, name })) {
        this.router.navigate(['/contracts'], { queryParams: { id: item?._id, name } });
      }
      return;
    }

    if (addOrEdit === 'edit_staff') {
      // Handle staff edit separately due to different route structure
      const userId = item?._id || item?.id;
      this.router.navigate(['/setting/office-staff/staff-edit', userId]);
      return;
    }

    // Navigate based on mapped edit routes
    if (addOrEdit in editRoutes) {
      const { path, params } = editRoutes[addOrEdit];
      if (!params || shouldNavigate(path, params)) {
        this.router.navigate([path], params ? { queryParams: params } : undefined);
      }
    }
  }

  @ViewChild(HeaderNotesComponent) headerNotes: HeaderNotesComponent;

  // Toggle the notes side panel (markup/logic now live in HeaderNotesComponent)
  openNote() {
    this.headerNotes?.openNote();
  }
}
